import { create } from 'zustand';
import { io } from 'socket.io-client';
import { notificationService } from '../services';
import useAuthStore from './useAuthStore';
import useShopStore from './useShopStore';
import useToastStore from './useToastStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
const SOCKET_URL = API_URL.replace('/api/v1', '');

// Global socket instance (outside of store to prevent re-creation)
let globalSocket = null;
let isConnecting = false;
let currentRoom = null;
let onNewNotificationCallback = null;
// Token con el que se abrio la conexion actual: si cambia, la conexion vieja
// pertenece a otra sesion y hay que rehacerla.
let currentToken = null;

const useNotificationStore = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  isConnected: false,

  // Initialize socket connection (called once)
  initSocket: (token, userRole, shopId, onNewNotification) => {
    // Si ya hay conexion pero con OTRO token, pertenece a la sesion anterior:
    // sin esto, cerrar sesion y entrar con otro usuario dejaba el socket viejo
    // vivo y el panel seguia reportando al usuario anterior.
    if (globalSocket && currentToken && token && currentToken !== token) {
      get().disconnectSocket();
    }

    // Prevent multiple connections
    if (globalSocket || isConnecting) {
      // If already connected, just switch room if shopId changed
      if (globalSocket && shopId) {
        get().switchRoom(shopId);
      }
      return;
    }

    // Todos los roles autenticados abren socket: los admins por las notificaciones
    // y todos (incluidos mecanicos) para aparecer en el panel de usuarios en linea.
    if (!token) {
      return;
    }

    isConnecting = true;
    currentToken = token;
    onNewNotificationCallback = onNewNotification;

    globalSocket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    globalSocket.on('connect', () => {
      console.log('[Socket] Connected to server');
      // Join room for the specific shop (solo admins reciben notificaciones)
      if (shopId && ['admin', 'superadmin'].includes(userRole)) {
        globalSocket.emit('join-admin-room', shopId);
        currentRoom = shopId;
        console.log(`[Socket] Joined admin room for shop: ${shopId}`);
      }
      // Reporta la pantalla actual para el panel de usuarios en linea
      globalSocket.emit('presence-activity', { path: window.location.pathname });
      set({ isConnected: true });
      isConnecting = false;
    });

    globalSocket.on('new-appointment', (notification) => {
      console.log('[Socket] New appointment received:', notification);
      get().addNotification(notification);
      if (onNewNotificationCallback) {
        onNewNotificationCallback(notification);
      }
    });

    // ── Comandos del superadmin sobre las sesiones ──
    // Recarga la pantalla. Se da un margen breve para que el usuario vea por que.
    globalSocket.on('force-reload', (payload) => {
      console.log('[Socket] Recarga remota solicitada');
      try {
        useToastStore.getState().info(
          payload?.message || 'El sistema fue actualizado. La pantalla se recargará.',
          4000
        );
      } catch {
        // El toast es cortesia: si falla, se recarga igual
      }
      setTimeout(() => window.location.reload(), 3000);
    });

    // Cierre de sesion forzado. El token ya quedo invalidado en el servidor,
    // asi que aunque el usuario ignore esto no podra seguir operando.
    globalSocket.on('force-logout', () => {
      console.log('[Socket] Cierre de sesion remoto solicitado');
      try {
        useShopStore.getState().clearShop();
        useAuthStore.getState().logout();
      } catch {
        // Da igual: la redireccion limpia el estado de todas formas
      }
      window.location.href = '/login?reason=session_ended';
    });

    globalSocket.on('disconnect', () => {
      console.log('[Socket] Disconnected from server');
      set({ isConnected: false });
    });

    globalSocket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message);
      isConnecting = false;
    });

    // Fetch initial notifications (endpoint admin-only)
    if (['admin', 'superadmin'].includes(userRole)) {
      get().fetchNotifications();
    }
  },

  // Reporta la ruta actual al backend (panel de usuarios en linea)
  reportActivity: (path) => {
    if (globalSocket?.connected) {
      globalSocket.emit('presence-activity', { path });
    }
  },

  // Switch room when superadmin changes shop
  switchRoom: (newShopId) => {
    if (!globalSocket || !newShopId || currentRoom === newShopId) {
      return;
    }

    // Leave current room and join new one
    if (currentRoom) {
      globalSocket.emit('leave-admin-room', currentRoom);
      console.log(`[Socket] Left admin room: ${currentRoom}`);
    }
    globalSocket.emit('join-admin-room', newShopId);
    currentRoom = newShopId;
    console.log(`[Socket] Switched to admin room for shop: ${newShopId}`);

    // Refresh notifications for the new shop
    get().fetchNotifications();
  },

  // Disconnect socket
  disconnectSocket: () => {
    if (globalSocket) {
      globalSocket.disconnect();
      globalSocket = null;
      isConnecting = false;
      currentRoom = null;
      currentToken = null;
      onNewNotificationCallback = null;
      set({ isConnected: false, notifications: [], unreadCount: 0 });
    }
  },

  // Fetch unread notifications
  fetchNotifications: async () => {
    try {
      set({ isLoading: true });
      const response = await notificationService.getUnread();
      if (response.success) {
        set({
          notifications: response.data.notifications,
          unreadCount: response.data.count,
        });
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  // Fetch unread count only
  fetchUnreadCount: async () => {
    try {
      const response = await notificationService.getUnreadCount();
      if (response.success) {
        set({ unreadCount: response.data.count });
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  },

  // Add new notification (from socket)
  addNotification: (notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
    }));
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n._id === notificationId ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  },

  // Mark all as read
  markAllAsRead: async () => {
    try {
      await notificationService.markAllAsRead();
      set((state) => ({
        notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  },

  // Clear all notifications (local only)
  clearNotifications: () => {
    set({ notifications: [], unreadCount: 0 });
  },
}));

/**
 * Acceso al socket global compartido (lo usa la vista de usuarios en linea
 * para escuchar `presence-updated` sin abrir una segunda conexion).
 */
export const getSocket = () => globalSocket;

export default useNotificationStore;
